import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useFittedVisibleTagCount } from "@/features/invoices/hooks/useFittedVisibleTagCount";
import { cn } from "@/utils/cn";

export interface MultiSelectOption {
  value: string;
  label: string;
  description?: string;
}

interface MultiSelectProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  allLabel?: string;
  maxCount?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  limitMessage?: string;
  /** Fixed tag count before +N; omit to auto-fit one row */
  maxVisibleTags?: number;
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel = "All",
  maxCount = 25,
  searchable,
  searchPlaceholder = "Search…",
  disabled,
  limitMessage,
  maxVisibleTags,
}: MultiSelectProps) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [limitVisible, setLimitVisible] = useState(false);

  const isAll = selected.length === 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.description?.toLowerCase().includes(q),
    );
  }, [options, search]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  const selectAll = () => {
    onChange([]);
    setLimitVisible(false);
  };

  const toggle = (value: string) => {
    if (isAll) {
      onChange([value]);
      setLimitVisible(false);
      return;
    }
    const has = selected.includes(value);
    if (!has && selected.length >= maxCount) {
      setLimitVisible(true);
      return;
    }
    setLimitVisible(false);
    const next = has
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next.length ? next : []);
  };

  const removeTag = (value: string) => {
    const next = selected.filter((v) => v !== value);
    onChange(next);
  };

  const tags = isAll
    ? [{ key: "__all__", label: allLabel, removable: false as const }]
    : selected.map((value) => {
        const opt = options.find((o) => o.value === value);
        return {
          key: value,
          label: opt?.label ?? value,
          removable: true as const,
          value,
        };
      });

  const fittedCount = useFittedVisibleTagCount(
    tags.length,
    tagsContainerRef,
    measureRef,
    maxVisibleTags,
  );
  const visibleTags = tags.slice(0, fittedCount);
  const hiddenCount = tags.length - visibleTags.length;

  return (
    <div ref={rootRef} className="relative min-w-0 w-full">
      <label
        htmlFor={id}
        className="mb-1 block text-[0.72rem] font-semibold text-[var(--muted)]"
      >
        {label}
      </label>
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn("filter-select-trigger", open && "is-open")}
        onClick={() => setOpen((o) => !o)}
      >
        <span ref={tagsContainerRef} className="filter-select-trigger__tags">
          {visibleTags.map((tag) => (
            <span
              key={tag.key}
              className={cn(
                "filter-select-tag",
                tag.key === "__all__"
                  ? "filter-select-tag--all"
                  : "filter-select-tag--default",
              )}
            >
              <span className="filter-select-tag__label">{tag.label}</span>
              {tag.removable ? (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Remove ${tag.label}`}
                  className="shrink-0 cursor-pointer text-[rgba(0,0,0,0.45)] hover:text-[var(--text)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag.value);
                  }}
                >
                  ×
                </span>
              ) : null}
            </span>
          ))}
          {hiddenCount > 0 ? (
            <span className="filter-select-tag filter-select-tag--muted shrink-0">
              +{hiddenCount}
            </span>
          ) : null}
          <span
            ref={measureRef}
            className="filter-select-trigger__measure"
            aria-hidden
          >
            {tags.map((tag) => (
              <span
                key={tag.key}
                data-measure-tag
                className={cn(
                  "filter-select-tag",
                  tag.key === "__all__"
                    ? "filter-select-tag--all"
                    : "filter-select-tag--default",
                )}
              >
                <span className="filter-select-tag__label">{tag.label}</span>
                {tag.removable ? (
                  <span className="shrink-0" aria-hidden>
                    ×
                  </span>
                ) : null}
              </span>
            ))}
            <span
              data-measure-overflow
              className="filter-select-tag filter-select-tag--muted shrink-0"
            >
              +9
            </span>
          </span>
        </span>
        <span
          className={cn(
            "pointer-events-none shrink-0 px-2 text-[rgba(0,0,0,0.45)] transition",
            open && "rotate-180 text-[var(--accent)]",
          )}
          aria-hidden
        >
          ▾
        </span>
      </button>

      {open ? (
        <ul
          role="listbox"
          aria-label={label}
          className="filter-select-dropdown absolute left-0 right-0 top-[calc(100%+4px)] z-[120] max-h-64 list-none overflow-y-auto rounded-lg border border-[var(--border-soft)] bg-white p-1 shadow-[0_6px_16px_rgba(0,0,0,0.08)]"
        >
          {searchable ? (
            <li className="sticky top-0 z-[1] bg-white px-2 pb-1 pt-2">
              <input
                type="search"
                placeholder={searchPlaceholder}
                className="w-full rounded-md border border-[#d9d9d9] px-3 py-1.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[rgba(15,118,110,0.12)]"
                value={search}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setSearch(e.target.value)}
              />
            </li>
          ) : null}
          <li>
            <button
              type="button"
              role="option"
              aria-selected={isAll}
              className={cn(
                "flex w-full items-center rounded px-3 py-2 text-left text-sm font-semibold",
                isAll ? "bg-[#e6fffb]" : "hover:bg-[rgba(0,0,0,0.04)]",
              )}
              onClick={(e) => {
                e.stopPropagation();
                selectAll();
              }}
            >
              {allLabel}
              {isAll ? <span className="ml-auto text-[var(--accent)]">✓</span> : null}
            </button>
          </li>
          <li className="my-0.5 border-b border-[var(--border-soft)]" aria-hidden />
          {filtered.map((opt) => {
            const on = !isAll && selected.includes(opt.value);
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={cn(
                    "flex w-full items-start gap-2 rounded px-3 py-2 text-left text-sm",
                    on ? "bg-[#e6fffb] font-semibold" : "hover:bg-[rgba(0,0,0,0.04)]",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(opt.value);
                  }}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block">{opt.label}</span>
                    {opt.description ? (
                      <span className="block text-xs font-normal text-[var(--muted)]">
                        {opt.description}
                      </span>
                    ) : null}
                  </span>
                  {on ? (
                    <span className="shrink-0 text-[var(--accent)]">✓</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {limitVisible && limitMessage ? (
        <p className="mt-1 text-xs font-semibold text-amber-700" role="status">
          {limitMessage}
        </p>
      ) : null}
    </div>
  );
}
